import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { produce } from 'immer'

import { FacebookPage, FacebookPost, GetFacebookFeedResponse } from '@3m5/sm_publish-frontend-api/models'

import { ApiError } from '../../store/postingRoot.reducer'

import {
  answerToFacebookPostComment,
  deletePost,
  deleteFacebookPostComment,
  getFacebookFeedForPage,
  getFacebookPage,
  getFacebookPost,
  likeFacebookPost,
  likeFacebookPostComment,
  unlikeFacebookPost,
  unlikeFacebookPostComment,
  writeFacebookPostComment,
} from './facebook.actions'

export interface FacebookState {
  fetching: boolean
  facebookPage?: FacebookPage
  facebookFeeds?: GetFacebookFeedResponse
  error: ApiError
}

const INITIAL_STATE: FacebookState = {
  fetching: false,
  error: {},
}

export const facebookReducer = reducerWithInitialState(INITIAL_STATE)

  /**
   * getFacebookPage
   */
  .case(getFacebookPage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getFacebookPage
    })
  })

  .case(getFacebookPage.done, (state, { result }) => {
    return produce(state, draft => {
      draft.facebookPage = result
      draft.fetching = false
    })
  })

  .case(getFacebookPage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getFacebookPage = (error as Response)
    })
  })

  /**
   * getFacebookFeedForPage
   */
  .case(getFacebookFeedForPage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.loadFacebookFeedForPage
    })
  })

  .case(getFacebookFeedForPage.done, (state, { result }) => {
    return produce(state, draft => {
      draft.facebookFeeds = result
      draft.fetching = false
    })
  })

  .case(getFacebookFeedForPage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.loadFacebookFeedForPage = (error as Response)
    })
  })

  /**
   * deletePost
   */
  .case(deletePost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.deletePost
    })
  })

  .case(deletePost.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(deletePost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.deletePost = (error as Response)
    })
  })

  /**
   * deleteFacebookPostComment
   */
  .case(deleteFacebookPostComment.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.deletePostComment
    })
  })

  .case(deleteFacebookPostComment.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(deleteFacebookPostComment.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.deletePostComment = (error as Response)
    })
  })

  /**
   * likeFacebookPost
   */
  .case(likeFacebookPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.likePost
    })
  })

  .case(likeFacebookPost.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(likeFacebookPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.likePost = (error as Response)
    })
  })

  /**
   * likeFacebookPostComment
   */
  .case(likeFacebookPostComment.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.likePostComment
    })
  })

  .case(likeFacebookPostComment.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(likeFacebookPostComment.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.likePostComment = (error as Response)
    })
  })

  /**
   * unlikeFacebookPost
   */
  .case(unlikeFacebookPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.unlikePost
    })
  })

  .case(unlikeFacebookPost.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(unlikeFacebookPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.unlikePost = (error as Response)
    })
  })

  /**
   * unlikeFacebookPostComment
   */
  .case(unlikeFacebookPostComment.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.unlikePostComment
    })
  })

  .case(unlikeFacebookPostComment.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(unlikeFacebookPostComment.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.unlikePostComment = (error as Response)
    })
  })

  /**
   * writeFacebookPostComment
   */
  .case(writeFacebookPostComment.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.writePostComment
    })
  })

  .case(writeFacebookPostComment.done, (state, { result }) => {
    // eslint-disable-next-line no-unused-vars
    const newFeeds: FacebookPost[] | undefined = state.facebookFeeds && state.facebookFeeds.posts
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(writeFacebookPostComment.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.writePostComment = (error as Response)
    })
  })

  /**
   * getFacebookPost
   */
  .case(getFacebookPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getFacebookPost
    })
  })

  .case(getFacebookPost.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(getFacebookPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getFacebookPost = (error as Response)
    })
  })

  /**
   * answerToFacebookPostComment
   */
  .case(answerToFacebookPostComment.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.answerToPostComment
    })
  })

  .case(answerToFacebookPostComment.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(answerToFacebookPostComment.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.answerToPostComment = (error as Response)
    })
  })
